"use client";

import { apiAddComment } from "@/apis/offers";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

interface RatingProps {
    offer_id: string;
    company_id: string;
}

const labels: Record<number, string> = {
    1: "Poor",
    2: "Fair",
    3: "Good",
    4: "Great",
    5: "Excellent",
};

const RateUs = ({ offer_id, company_id }: RatingProps) => {
    const [rating, setRating] = useState<number>(0);
    const [hovered, setHovered] = useState<number>(0);
    const [comment, setComment] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [hasRated, setHasRated] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const lastRated = localStorage.getItem(`hasRated_${offer_id}`);
        if (lastRated && new Date().getTime() - parseInt(lastRated) < 86400000) {
            setHasRated(true);
            setSubmitted(true);
        }
    }, [offer_id]);

    const handleRate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (hasRated) {
            toast.error("You have already rated, please try again later!", { autoClose: 2000 });
            return;
        }
        if (!rating && !comment.trim()) {
            toast.error("Please select a rating or add a comment.", { autoClose: 2000 });
            return;
        }
        setLoading(true);
        try {
            const response = await apiAddComment(offer_id, company_id, comment, rating.toString());
            if (response.status === "success" || (response as any).status === 200) {
                toast.success("Thank you for your feedback!", { autoClose: 2000 });
                setRating(0);
                setComment("");
                localStorage.setItem(`hasRated_${offer_id}`, new Date().getTime().toString());
                setHasRated(true);
                setSubmitted(true);
            } else {
                toast.error("An error occurred. Please try again later!", { autoClose: 2000 });
            }
        } catch {
            toast.error("An error occurred while submitting feedback.", { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    const activeRating = hovered || rating;

    /* ── Submitted state ── */
    if (submitted) {
        return (
            <div className="flex flex-col items-center justify-center py-6 gap-3 text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(74,154,110,0.1)" }}>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="#5D27A1" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800 m-0">Thanks for your feedback</p>
                <p className="text-xs text-gray-400 m-0">Your rating helps others find great deals.</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleRate} autoComplete="off" className="flex flex-col gap-5" style={{ fontFamily: "'DM Sans', sans-serif" }}>

            {/* Star row */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Your Rating</span>
                    {activeRating > 0 && (
                        <span
                            className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full transition-all duration-200"
                            style={{ background: "rgba(74,154,110,0.1)", color: "#5D27A1", border: "1px solid rgba(74,154,110,0.15)" }}
                        >
                            {labels[activeRating]}
                        </span>
                    )}
                </div>

                {/* Stars */}
                <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => {
                        const filled = star <= activeRating;
                        return (
                            <button
                                key={star}
                                type="button"
                                onClick={() => setRating(star)}
                                onMouseEnter={() => setHovered(star)}
                                onMouseLeave={() => setHovered(0)}
                                className="focus:outline-none transition-transform duration-150 active:scale-90 hover:scale-110"
                                aria-label={`Rate ${star} stars`}
                            >
                                <svg
                                    className="w-7 h-7 transition-all duration-200"
                                    viewBox="0 0 24 24"
                                    fill={filled ? "#5D27A1" : "none"}
                                    stroke={filled ? "#5D27A1" : "#d1d5db"}
                                    strokeWidth={1.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                                    />
                                </svg>
                            </button>
                        );
                    })}
                </div>

                {/* Progress indicator */}
                <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                        <div
                            key={s}
                            className="h-0.5 flex-1 rounded-full transition-all duration-200"
                            style={{ background: s <= activeRating ? "#5D27A1" : "#e5e7eb" }}
                        />
                    ))}
                </div>
            </div>

            {/* Comment */}
            <div className="flex flex-col gap-2">
                <label htmlFor="rate-comment" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Comment <span className="normal-case text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                    id="rate-comment"
                    placeholder="Share your experience..."
                    rows={3}
                    className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-white text-gray-700 resize-none outline-none transition-all duration-200 focus:border-[#5D27A1] placeholder-gray-400"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </div>

            {/* Submit */}
            <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
                style={{ background: "#5D27A1", boxShadow: loading ? "none" : "0 4px 14px rgba(74,154,110,0.25)" }}
            >
                {loading ? (
                    <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Submitting...
                    </span>
                ) : (
                    "Submit Feedback"
                )}
            </button>
        </form>
    );
};

export default RateUs;
